import _ from 'lodash';
import { mapGetters } from 'vuex';
import { BASE_PLAN_TYPES, planTypes } from '@/common/constants';
import { PBX_REGIONS_MAP_COUNTRY } from '@/plans/ZOOM_PHONE/helper/constants';
import { pageConfig } from '@/common/service/page-config';
import { logger } from '@/utils/log';
import { couponFetchStatus } from '@/views/config/coupon/helper/constant';

const softBundleTrackingState = {
  productView: false,
};

const trackingTypes = {
  [planTypes.ZOOM_PHONE]: 'phone',
  [planTypes.ZOOM_MEETING]: 'meeting',
};

const getPbxRegionMap = (key) => {
  return _.chain(PBX_REGIONS_MAP_COUNTRY)
    .get([key], [key])
    .join('&')
    .value();
};

const combineTrackingCode = (...args) => {
  return _.chain(args)
    .filter(Boolean)
    .map((p = '') => p.toLowerCase())
    .join('-')
    .value();
};

const sortPlanTypes = (x = []) => {
  return x.sort((a, b) => {
    const indexA = BASE_PLAN_TYPES.indexOf(a?.planType);
    const indexB = BASE_PLAN_TYPES.indexOf(b?.planType);

    const priorityA = indexA === -1 ? Infinity : indexA;
    const priorityB = indexB === -1 ? Infinity : indexB;

    return priorityA - priorityB;
  });
};

const getSoftBundleCode = (discountPlans = {}, getSelectedPlansMap = {}) => {
  const x = _.chain(discountPlans)
    .keys()
    .reduce((pre, planCode) => {
      const plan = getSelectedPlansMap[planCode] || {};
      const planType = plan?.type;
      const trackingType = trackingTypes[planType] ?? '';
      const category = plan?.category;
      const region = getPbxRegionMap(plan?.region ?? '');
      const billingCycle = plan?.billing_cycle;
      const code = combineTrackingCode(trackingType, category, region, billingCycle);

      return [...pre, {
        planType,
        code,
      }];
    }, [])
    .value();

  const y = sortPlanTypes(x);

  const softBundleCode = _.chain(y)
    .map((p) => p?.code)
    .filter(Boolean)
    .join('$')
    .value();

  return _.chain([pageConfig?.jointsale, softBundleCode])
    .filter(Boolean)
    .join('|')
    .value();
};

export default {
  computed: {
    ...mapGetters(['partnerRefer', 'verifyCouponStatus', 'discountWithAmountMap', 'getSelectedPlansMap']),
    useInSoftBundle() {
      return this.partnerRefer?.features?.useInSoftBundle;
    },
    softBundleInfo() {
      const isSoftBundle = this.useInSoftBundle && this.verifyCouponStatus === couponFetchStatus.successfully;
      const discountPlans = this.discountWithAmountMap?.discountPlans;

      return {
        isSoftBundle: isSoftBundle,
        softBundleCode: isSoftBundle ? getSoftBundleCode(discountPlans, this.getSelectedPlansMap) : '',
      };
    },
  },
  methods: {
    getSoftBundleTrackingInfoForProductView() {
      try {
        if (softBundleTrackingState.productView) {
          return this.softBundleInfo;
        }

        softBundleTrackingState.productView = true;

        const { discountInfo, discountInfoExtension } = this.partnerRefer?.coupon || {};
        const mergedDiscountInfo = { ...discountInfo, ...discountInfoExtension };
        const discountPlans = _.chain(this.getSelectedPlansMap)
          .keys()
          .reduce((pre, planCode) => {
            const discountPlan = _.get(mergedDiscountInfo, [planCode]);
            const discount = discountPlan?.discount;

            return {
              ...pre,
              ...(discount ? { [planCode]: discountPlan } : {}),
            };
          }, {})
          .value();

        return {
          isSoftBundle: this.useInSoftBundle,
          softBundleCode: this.useInSoftBundle ? getSoftBundleCode(discountPlans, this.getSelectedPlansMap) : '',
        };
      } catch (e) {
        logger.error('getSoftBundleTrackingInfoForProductView', e);
        return {};
      }
    },
    getSoftBundleInfoForValidated() {
      try {
        return this.softBundleInfo;
      } catch (e) {
        logger.error('getSoftBundleInfoForValidated', e);
        return {};
      }
    },
  },
};
